import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import { appBarHeight, navBarHeight } from '../../Constants';
import Item from '../../Models/ItemModel';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import { faArrowLeft, faComment, faEllipsisVertical, faFolder, faImage } from '@fortawesome/free-solid-svg-icons';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateLastActivity } from '../../Utils/LastActivity';
import ItemType from '../../Enums/ItemType';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import ImageItem from '../../Models/ImageItemModel';
import ImageItemSubTab from './ImageItemSubTab';
import ChecklistItemSubTab from './ChecklistItemSubTab';
import CommentItemSubTab from './CommentItemSubTab';
import { datetimeToApiString, datetimeToVisualString } from '../../Utils/DateTimeParser';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CommentItem from '../../Models/CommentItemModel';
import type ChecklistModel from '../../Models/Checklists/ChecklistModelModel';
import AddFolderDialog from '../../Dialogs/FolderDialogs/AddFolderDialog';
import FilledChecklist from '../../Models/Checklists/FilledChecklistModel';
import FilledChecklistValueObject from '../../Models/Checklists/FilledChecklistValueObjectModel';
import type ChecklistElement from '../../Models/Checklists/Elements/ChecklistElementModel';
import TextFieldChecklistElement from '../../Models/Checklists/Elements/TextFieldChecklistElementModel';
import DropdownChecklistElement from '../../Models/Checklists/Elements/DropdownChecklistElementModel';
import UserChecklistElement from '../../Models/Checklists/Elements/UserChecklistElementModel';
import DateChecklistElement from '../../Models/Checklists/Elements/DateChecklistElementModel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import MaskChecklistElement from '../../Models/Checklists/Elements/MaskChecklistElementModel';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { getUniqueId } from 'shouldve-been-js';
import { useTitle, toggleSelectRow, toggleSelectAll } from 'shouldve-been-react';
import { ConfirmDialog, ErrorSnackbar, SuccessSnackbar } from 'shouldve-been-mui';
import Folder from '../../Models/FolderModel';
import type MemoTemplate from '../../Models/MemoTemplateModel';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import RenameFolderDialog from '../../Dialogs/FolderDialogs/RenameFolderDialog';
import FolderInfoDialog from '../../Dialogs/FolderDialogs/FolderInfoDialog';
import MoveItemsDialog from '../../Dialogs/FolderDialogs/MoveItemsDialog';
import FilledObservation from '../../Models/FilledObservationModel';
import type ObservationModel from '../../Models/ObservationModelModel';
import ObservationItemSubTab from './ObservationItemSubTab';

type ItemsTabProps = {
    openedId: string | null, //this could either be an item or a folder
};

function ItemsTab(props: Readonly<ItemsTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Items');

    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const currentTheme: Theme = useTheme();

    const navigate: NavigateFunction = useNavigate();

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);
    const [successSnackbarMessage, setSuccessSnackbarMessage] = useState<string | null>(null);
    const [showAddFolderDialog, setShowAddFolderDialog] = useState<boolean>(false);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [anchorElTableHeader, setAnchorElTableHeader] = useState<HTMLElement | null>(null);
    const [anchorElOpenedFolder, setAnchorElOpenedFolder] = useState<HTMLElement | null>(null);
    const [showConfirmDeletionDialog, setShowConfirmDeletionDialog] = useState<boolean>(false);
    const [showConfirmUploadDialog, setShowConfirmUploadDialog] = useState<boolean>(false);
    const [showRenameFolderDialog, setShowRenameFolderDialog] = useState<boolean>(false);
    const [showFolderInfoDialog, setShowFolderInfoDialog] = useState<boolean>(false);
    const [showConfirmUploadFolderDialog, setShowConfirmUploadFolderDialog] = useState<boolean>(false);
    const [showConfirmRemoveFolderAfterUploadDialog, setShowConfirmRemoveFolderAfterUploadDialog] = useState<boolean>(false);
    const [showConfirmRemoveAllSelectedFoldersAfterUploadDialog, setShowConfirmRemoveAllSelectedFoldersAfterUploadDialog] = useState<boolean>(false);
    const [showConfirmDeletionFolderDialog, setShowConfirmDeletionFolderDialog] = useState<boolean>(false);
    const [showMoveItemsDialog, setShowMoveItemsDialog] = useState<boolean>(false);

    const [checklistModels, setChecklistModels] = useState<ChecklistModel[]>([]);
    const [filledChecklists, setFilledChecklists] = useState<FilledChecklist[]>([]);
    const [observationModels, setObservationModels] = useState<ObservationModel[]>([]);
    const [filledObservations, setFilledObservations] = useState<FilledObservation[]>([]);
    const [memoTemplates, setMemoTemplates] = useState<MemoTemplate[]>([]);

    const [commentItems, setCommentItems] = useState<CommentItem[]>([]);
    const [imageItems, setImageItems] = useState<ImageItem[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);

    const [checklistItemsRerenderFlag, setChecklistItemsRerenderFlag] = useState<Date>(new Date());
    const [observationItemsRerenderFlag, setObservationItemsRerenderFlag] = useState<Date>(new Date());
    const [commentItemsRerenderFlag, setCommentItemsRerenderFlag] = useState<Date>(new Date());
    const [imageItemsRerenderFlag, setImageItemsRerenderFlag] = useState<Date>(new Date());
    const [foldersRerenderFlag, setFoldersRerenderFlag] = useState<Date>(new Date());

    const openMenuTableHeader: boolean = Boolean(anchorElTableHeader);
    const openMenuOpenedFolder: boolean = Boolean(anchorElOpenedFolder);

    const openedItem: Item | null = useMemo<Item | null>(() => {
        
        const matchingFilledChecklist: FilledChecklist | null = filledChecklists.find((currentFilledChecklist: FilledChecklist) => currentFilledChecklist.filledChecklistId === props.openedId) ?? null;
        
        if (matchingFilledChecklist !== null) {
            return new Item(matchingFilledChecklist.filledChecklistId, matchingFilledChecklist.checklistId, ItemType.CHECKLIST, matchingFilledChecklist.folderId, matchingFilledChecklist.sortIndex);
        }

        const matchingFilledObservation: FilledObservation | null = filledObservations.find((currentFilledObservation: FilledObservation) => currentFilledObservation.filledObservationId === props.openedId) ?? null;
        
        if (matchingFilledObservation !== null) {
            return new Item(matchingFilledObservation.filledObservationId, matchingFilledObservation.observationId, ItemType.OBSERVATION, matchingFilledObservation.folderId, matchingFilledObservation.sortIndex);
        }

        const matchingCommentItem: CommentItem | null = commentItems.find((currentCommentItem: CommentItem) => currentCommentItem.commentItemId === props.openedId) ?? null;
        
        if (matchingCommentItem !== null) {
            return new Item(matchingCommentItem.commentItemId, null, ItemType.COMMENT, matchingCommentItem.folderId, null);
        }

        const matchingImageItem: ImageItem | null = imageItems.find((currentImageItem: ImageItem) => currentImageItem.imageItemId === props.openedId) ?? null;

        if (matchingImageItem !== null) {
            return new Item(matchingImageItem.imageItemId, null, ItemType.IMAGE, matchingImageItem.folderId, null);
        }

        return null;
    }, [commentItems, filledChecklists, filledObservations, imageItems, props.openedId]);

    const openedFolder: Folder | null = useMemo<Folder | null>(() => {

        return folders.find((currentFolder: Folder) => currentFolder.folderId === props.openedId) ?? null;
    }, [folders, props.openedId]);

    const relevantFolder: Folder | null = useMemo<Folder | null>(() => { //difference between this one and the one above is that this one holds a folder also if an item is opened from within a folder

        if (openedFolder !== null) {
            return openedFolder;
        }

        return folders.find((currentFolder: Folder) => currentFolder.folderId === openedItem?.folderId) ?? null;

    }, [folders, openedFolder, openedItem?.folderId]);

    const allRowIdsInScope: string[] = useMemo<string[]>(() => {

        if (openedFolder === null) {
            return folders.map((folder: Folder) => folder.folderId).concat(commentItems.map((commentItem: CommentItem) => commentItem.commentItemId)).concat(imageItems.map((imageItem: ImageItem) => imageItem.imageItemId));
        } else {
            return filledChecklists.filter((filledChecklist: FilledChecklist) => filledChecklist.folderId === openedFolder.folderId).map((filledChecklist: FilledChecklist) => filledChecklist.filledChecklistId).concat(filledObservations.filter((filledObservation: FilledObservation) => filledObservation.folderId === openedFolder.folderId).map((filledObservation: FilledObservation) => filledObservation.filledObservationId)).concat(commentItems.filter((commentItem: CommentItem) => commentItem.folderId === openedFolder.folderId).map((commentItem: CommentItem) => commentItem.commentItemId)).concat(imageItems.filter((imageItem: ImageItem) => imageItem.folderId === openedFolder.folderId).map((imageItem: ImageItem) => imageItem.imageItemId));
        }
    }, [commentItems, filledChecklists, filledObservations, folders, imageItems, openedFolder]);

    const tableRows: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const folderRows: JSX.Element[] = openedFolder === null
            ? [...folders].sort((a: Folder, b: Folder) => b.changedDate.getTime() - a.changedDate.getTime()).map((folder: Folder) => {
                return (
                    <TableRow
                        key={folder.folderId}
                        onClick={(): void => {
                            setSelectedRows([]);
                            navigate('/items/' + folder.folderId);
                        }}
                        style={{
                            cursor: 'pointer',
                        }}
                    >
                        <TableCell style={{
                            padding: '5px 0px 5px 5px',
                        }}>
                            <Checkbox
                                color='primary'
                                checked={selectedRows.includes(folder.folderId)}
                                onChange={(): void => toggleSelectRow(folder.folderId, selectedRows, setSelectedRows, null)}
                                onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                            />
                        </TableCell>
                        <TableCell>
                            <Typography style={{
                                wordWrap: 'break-word',
                                fontSize: '13px',
                            }}>
                                Folder
                            </Typography>
                        </TableCell>
                        <TableCell colSpan={2}>
                            <Typography style={{
                                wordWrap: 'break-word',
                                width: Math.min(windowSize[0] * 0.5, 500),
                                fontSize: '13px',
                            }}>
                                {folder.name}
                            </Typography>
                        </TableCell>
                    </TableRow>
                );
            })
            : []; 
        
        return folderRows.concat([...filledChecklists].filter((filledChecklist: FilledChecklist) => filledChecklist.folderId === (openedFolder?.folderId ?? null)).sort((a: FilledChecklist, b: FilledChecklist) => a.sortIndex - b.sortIndex).map((filledChecklist: FilledChecklist) => {

            return (
                <TableRow
                    key={filledChecklist.filledChecklistId}
                    onClick={(): void => navigate('/items/' + filledChecklist.filledChecklistId)}
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <TableCell>
                    </TableCell>
                    <TableCell>
                        <Typography style={{
                            wordWrap: 'break-word',
                            fontSize: '13px',
                        }}>
                            Checklist
                        </Typography>
                    </TableCell>
                    <TableCell colSpan={2}>
                        <Typography style={{
                            wordWrap: 'break-word',
                            width: Math.min(windowSize[0] * 0.5, 500),
                            fontSize: '13px',
                        }}>
                            {filledChecklist.name}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        })).concat([...filledObservations].filter((filledObservation: FilledObservation) => filledObservation.folderId === (openedFolder?.folderId ?? null)).sort((a: FilledObservation, b: FilledObservation) => a.sortIndex - b.sortIndex).map((filledObservation: FilledObservation) => {

            return (
                <TableRow
                    key={filledObservation.filledObservationId}
                    onClick={(): void => navigate('/items/' + filledObservation.filledObservationId)}
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <TableCell>
                    </TableCell>
                    <TableCell>
                        <Typography style={{
                            wordWrap: 'break-word',
                            fontSize: '13px',
                        }}>
                            Observation
                        </Typography>
                    </TableCell>
                    <TableCell colSpan={2}>
                        <Typography style={{
                            wordWrap: 'break-word',
                            width: Math.min(windowSize[0] * 0.5, 500),
                            fontSize: '13px',
                        }}>
                            {filledObservation.name}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        })).concat([...commentItems].filter((commentItem: CommentItem) => commentItem.folderId === (openedFolder?.folderId ?? null)).sort((a: CommentItem, b: CommentItem) => b.changedDate.getTime() - a.changedDate.getTime()).map((commentItem: CommentItem) => {

            return (
                <TableRow
                    key={commentItem.commentItemId}
                    onClick={(): void => navigate('/items/' + commentItem.commentItemId)}
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <TableCell style={{
                        padding: '5px 0px 5px 5px',
                    }}>
                        <Checkbox
                            color='primary'
                            checked={selectedRows.includes(commentItem.commentItemId)}
                            onChange={(): void => toggleSelectRow(commentItem.commentItemId, selectedRows, setSelectedRows, null)}
                            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                        />
                    </TableCell>
                    <TableCell>
                        <Typography style={{
                            wordWrap: 'break-word',
                            fontSize: '13px',
                        }}>
                            Comment
                        </Typography>
                    </TableCell>
                    <TableCell colSpan={2}>
                        <Typography style={{
                            wordWrap: 'break-word',
                            width: Math.min(windowSize[0] * 0.5, 500),
                            fontSize: '13px',
                        }}>
                            {commentItem.name}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        })).concat([...imageItems].filter((imageItem: ImageItem) => imageItem.folderId === (openedFolder?.folderId ?? null)).sort((a: ImageItem, b: ImageItem) => b.changedDate.getTime() - a.changedDate.getTime()).map((imageItem: ImageItem) => {

            return (
                <TableRow
                    key={imageItem.imageItemId}
                    onClick={(): void => navigate('/items/' + imageItem.imageItemId)}
                    style={{
                        cursor: 'pointer',
                    }}
                >
                    <TableCell style={{
                        padding: '5px 0px 5px 5px',
                    }}>
                        <Checkbox
                            color='primary'
                            checked={selectedRows.includes(imageItem.imageItemId)}
                            onChange={(): void => toggleSelectRow(imageItem.imageItemId, selectedRows, setSelectedRows, null)}
                            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                        />
                    </TableCell>
                    <TableCell>
                        <Typography style={{
                            wordWrap: 'break-word',
                            fontSize: '13px',
                        }}>
                            Image
                        </Typography>
                    </TableCell>
                    <TableCell colSpan={2}>
                        <Typography style={{
                            wordWrap: 'break-word',
                            width: Math.min(windowSize[0] * 0.5, 500),
                            fontSize: '13px',
                        }}>
                            {imageItem.name}
                        </Typography>
                    </TableCell>
                </TableRow>
            );
        }));
    }, [openedFolder, folders, filledChecklists, filledObservations, commentItems, imageItems, selectedRows, windowSize, navigate]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => { //fetch checklistmodels

        let mounted: boolean = true;
        
        async function fetchChecklistModelsFromIndexedDB(): Promise<void> {

            let newChecklistModels: ChecklistModel[] = await indexedDBContext.indexedDBService.getAllChecklistModels();

            if (newChecklistModels.length === 0) { //as this is the start view, this might be retrieved before the api call to get the models are done first time logging in (if the user is quick to create pin), this is a fix for that
                await new Promise(r => setTimeout(r, 3000));
                newChecklistModels = await indexedDBContext.indexedDBService.getAllChecklistModels();
                if (newChecklistModels.length === 0) {
                    await new Promise(r => setTimeout(r, 8000));
                    newChecklistModels = await indexedDBContext.indexedDBService.getAllChecklistModels();
                }
            }

            if (mounted) {
                setChecklistModels(newChecklistModels);
            }
        }

        void fetchChecklistModelsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService]);

    useEffect(() => { //fetch observationmodels

        let mounted: boolean = true;
        
        async function fetchObservationModelsFromIndexedDB(): Promise<void> {

            let newObservationModels: ObservationModel[] = await indexedDBContext.indexedDBService.getAllObservationModels();

            if (newObservationModels.length === 0) { //as this is the start view, this might be retrieved before the api call to get the models are done first time logging in (if the user is quick to create pin), this is a fix for that
                await new Promise(r => setTimeout(r, 3000));
                newObservationModels = await indexedDBContext.indexedDBService.getAllObservationModels();
                if (newObservationModels.length === 0) {
                    await new Promise(r => setTimeout(r, 8000));
                    newObservationModels = await indexedDBContext.indexedDBService.getAllObservationModels();
                }
            }

            if (mounted) {
                setObservationModels(newObservationModels);
            }
        }

        void fetchObservationModelsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService]);

    useEffect(() => { //fetch memotemplates
        let mounted: boolean = true;
        
        async function fetchMemoTemplatesFromIndexedDB(): Promise<void> {

            let newMemoTemplates: MemoTemplate[] = await indexedDBContext.indexedDBService.getAllMemoTemplates();

            if (newMemoTemplates.length === 0) { //as this is the start view, this might be retrieved before the api call to get the templates are done first time logging in (if the user is quick to create pin), this is a fix for that
                await new Promise(r => setTimeout(r, 3000));
                newMemoTemplates = await indexedDBContext.indexedDBService.getAllMemoTemplates();
                if (newMemoTemplates.length === 0) {
                    await new Promise(r => setTimeout(r, 8000));
                    newMemoTemplates = await indexedDBContext.indexedDBService.getAllMemoTemplates();
                }
            }

            if (mounted) {
                setMemoTemplates(newMemoTemplates);
            }
        }

        void fetchMemoTemplatesFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, checklistItemsRerenderFlag, observationItemsRerenderFlag]);

    useEffect(() => { //fetch filled checklists

        let mounted: boolean = true;
        
        async function fetchFilledChecklistsFromIndexedDB(): Promise<void> {

            const newFilledChecklists: FilledChecklist[] = await indexedDBContext.indexedDBService.getAllFilledChecklists();

            if (mounted) {
                setFilledChecklists(newFilledChecklists);
            }
        }

        void fetchFilledChecklistsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, checklistItemsRerenderFlag]);

    useEffect(() => { //fetch filled observations

        let mounted: boolean = true;
        
        async function fetchFilledObservationsFromIndexedDB(): Promise<void> {

            const newFilledObservations: FilledObservation[] = await indexedDBContext.indexedDBService.getAllFilledObservations();

            if (mounted) {
                setFilledObservations(newFilledObservations);
            }
        }

        void fetchFilledObservationsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, observationItemsRerenderFlag]);

    useEffect(() => { //fetch comment items

        let mounted: boolean = true;
        
        async function fetchCommentItemsFromIndexedDB(): Promise<void> {

            const newCommentItems: CommentItem[] = await indexedDBContext.indexedDBService.getAllCommentItems();

            if (mounted) {
                setCommentItems(newCommentItems);
                setSelectedRows([]);
            }
        }

        void fetchCommentItemsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, commentItemsRerenderFlag]);

    useEffect(() => { //fetch image items

        let mounted: boolean = true;
        
        async function fetchImageItemsFromIndexedDB(): Promise<void> {

            const newImageItems: ImageItem[] = await indexedDBContext.indexedDBService.getAllImageItems();

            if (mounted) {
                setImageItems(newImageItems);
                setSelectedRows([]);
            }
        }

        void fetchImageItemsFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, imageItemsRerenderFlag]);

    useEffect(() => { //fetch folders

        let mounted: boolean = true;
        
        async function fetchFoldersFromIndexedDB(): Promise<void> {

            const newFolders: Folder[] = await indexedDBContext.indexedDBService.getAllFolders();

            if (mounted) {
                setFolders(newFolders);
                setSelectedRows([]);
            }
        }

        void fetchFoldersFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, foldersRerenderFlag]);

    function handleAddNewFolder(): void {

        updateLastActivity(userContext, localStorageContext);

        setOpenSpeedDial(false);

        setShowAddFolderDialog(true);
    }

    async function handleCloseAddFolderDialog(shouldAdd: boolean, folderType: string, templateId: string | null, name: string): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        setShowAddFolderDialog(false);

        if (!shouldAdd) {
            return;
        }

        const newFolderId: string = getUniqueId();

        const createdDate: Date = new Date();

        const newFolder: Folder = new Folder(newFolderId, folderType === 'template' ? templateId : null, name, createdDate, createdDate);

        const folderSuccess: boolean = await indexedDBContext.indexedDBService.insertFolder(newFolder);

        if (!folderSuccess) {
            setErrorSnackbarMessage('Failed to create new folder');
            return;
        }

        if (folderType === 'template') {

            const template: MemoTemplate | null = memoTemplates.find((currentTemplate: MemoTemplate) => currentTemplate.templateId === templateId) ?? null;
        
            if (template === null) {
                setErrorSnackbarMessage('Folder created, but is empty as template could not be found');
                return;
            }

            for (let i: number = 0; i < template.tabs.length; i++) {

                const checklistModel: ChecklistModel | null = checklistModels.find((currentChecklistModel: ChecklistModel) => currentChecklistModel.checklistId === template.tabs[i]?.tabId) ?? null;

                if (checklistModel !== null) { //add tabs that are checklists (including custom)
                    
                    // eslint-disable-next-line no-await-in-loop
                    const checklistSuccess: boolean = await handleAddFilledChecklist(checklistModel, template.tabs[i]?.caption ?? '', newFolderId, createdDate, template.tabs[i]?.index ?? -1);
                
                    if (!checklistSuccess) {
                        setErrorSnackbarMessage('Failed to create checklist in folder (but the folder itself has been created)');
                        return;
                    }
                }

                const observationModel: ObservationModel | null = observationModels.find((currentObservationModel: ObservationModel) => currentObservationModel.observationId === template.tabs[i]?.tabId) ?? null;

                if (observationModel !== null) { //add tabs that are observations
                    
                    // eslint-disable-next-line no-await-in-loop
                    const observationSuccess: boolean = await handleAddFilledObservation(observationModel, template.tabs[i]?.caption ?? '', newFolderId, createdDate, template.tabs[i]?.index ?? -1);
                
                    if (!observationSuccess) {
                        setErrorSnackbarMessage('Failed to create observation in folder (but the folder itself has been created)');
                        return;
                    }
                }
            }
        }

        setFoldersRerenderFlag(new Date());
        setChecklistItemsRerenderFlag(new Date());
        setObservationItemsRerenderFlag(new Date());

        navigate('/items/' + newFolderId); //this will only work if the rerenders above is finished executing - can do this another way if this becomes a problem
    }

    function handleCloseRenameFolderDialog(): void {

        setShowRenameFolderDialog(false);

        setFoldersRerenderFlag(new Date());
    }

    function handleCloseMoveItemsDialog(): void {

        setShowMoveItemsDialog(false);

        setCommentItemsRerenderFlag(new Date());
        setImageItemsRerenderFlag(new Date());

        setSelectedRows([]);
    }

    async function handleAddFilledChecklist(checklistModel: ChecklistModel, name: string, folderId: string, createdDate: Date, sortIndex: number): Promise<boolean> {

        const newFilledChecklistId: string = getUniqueId();

        const createdDateWithoutTime: Date = new Date(createdDate.getTime()); //copy date to not mutate original
        createdDateWithoutTime.setUTCHours(0, 0, 0, 0);

        //create values that should have a default value
        const values: FilledChecklistValueObject[] = [];
        
        checklistModel.elements.forEach((element: ChecklistElement) => {

            if (element instanceof TextFieldChecklistElement) {

                if (element.defaultValue !== null) {
                    values.push(new FilledChecklistValueObject(element.elementId, element.defaultValue, null, null));
                }
            } else if (element instanceof DropdownChecklistElement) {

                if (element.defaultValue !== null) {
                    values.push(new FilledChecklistValueObject(element.elementId, element.defaultValue, null, null)); //currently assumes the default value is allowed with the default selection of a dependent element - something else makes little sense
                }
            } else if (element instanceof UserChecklistElement) {

                if (element.currentAsDefault) {
                    values.push(new FilledChecklistValueObject(element.elementId, userContext.currentUser?.userId ?? null, null, null));
                }
            } else if (element instanceof DateChecklistElement) {

                if (element.currentAsDefault) {
                    values.push(new FilledChecklistValueObject(element.elementId, datetimeToApiString(createdDateWithoutTime, 0), null, null));
                }
            }
        });

        const newFilledChecklist: FilledChecklist = new FilledChecklist(newFilledChecklistId, checklistModel.checklistId, name, createdDate, createdDate, values, folderId, sortIndex);

        const success: boolean = await indexedDBContext.indexedDBService.insertFilledChecklist(newFilledChecklist);

        return success;
    }

    async function handleAddFilledObservation(observationModel: ObservationModel, name: string, folderId: string, createdDate: Date, sortIndex: number): Promise<boolean> {

        const newFilledObservationId: string = getUniqueId();

        const createdDateWithoutTime: Date = new Date(createdDate.getTime()); //copy date to not mutate original
        createdDateWithoutTime.setUTCHours(0, 0, 0, 0);

        const newFilledObservation: FilledObservation = new FilledObservation(newFilledObservationId, observationModel.observationId, name, createdDate, createdDate, [], folderId, sortIndex, '');

        const success: boolean = await indexedDBContext.indexedDBService.insertFilledObservation(newFilledObservation);

        return success;
    }

    async function handleAddNewComment(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        setOpenSpeedDial(false);

        const newCommentId: string = getUniqueId();

        const createdDate: Date = new Date();

        const newCommentItem: CommentItem = new CommentItem(newCommentId, datetimeToVisualString(createdDate, false, systemContext.serverTimezoneOffsetMinutes ?? 0), createdDate, createdDate, '', openedFolder?.folderId ?? null);

        const success: boolean = await indexedDBContext.indexedDBService.insertCommentItem(newCommentItem);

        if (!success) {
            setErrorSnackbarMessage('Failed to create new comment item');
            return;
        }

        setCommentItemsRerenderFlag(new Date());

        navigate('/items/' + newCommentId); //this will only work if the rerender above is finished executing - can do this another way if this becomes a problem
    }

    async function handleAddNewImage(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        setOpenSpeedDial(false);

        const newImageId: string = getUniqueId();

        const createdDate: Date = new Date();

        const newImageItem: ImageItem = new ImageItem(newImageId, datetimeToVisualString(createdDate, false, systemContext.serverTimezoneOffsetMinutes ?? 0), createdDate, createdDate, null, null, openedFolder?.folderId ?? null);

        const success: boolean = await indexedDBContext.indexedDBService.insertImageItem(newImageItem);

        if (!success) {
            setErrorSnackbarMessage('Failed to create new image item');
            return;
        }

        setImageItemsRerenderFlag(new Date());

        navigate('/items/' + newImageId); //this will only work if the rerender above is finished executing - can do this another way if this becomes a problem
    }

    function handleCloseFilledChecklist(successMessage: string | null): void {

        if (relevantFolder !== null) {
            navigate('/items/' + relevantFolder.folderId);
        } else {
            navigate('/items');
        }

        setChecklistItemsRerenderFlag(new Date());

        setSuccessSnackbarMessage(successMessage);

        setSelectedRows([]);
    }

    function handleCloseFilledObservation(successMessage: string | null): void {

        if (relevantFolder !== null) {
            navigate('/items/' + relevantFolder.folderId);
        } else {
            navigate('/items');
        }

        setObservationItemsRerenderFlag(new Date());

        setSuccessSnackbarMessage(successMessage);

        setSelectedRows([]);
    }

    function handleCloseCommentItem(successMessage: string | null): void {

        if (relevantFolder !== null) {
            navigate('/items/' + relevantFolder.folderId);
        } else {
            navigate('/items');
        }

        setCommentItemsRerenderFlag(new Date());

        setSuccessSnackbarMessage(successMessage);

        setSelectedRows([]);
    }

    function handleCloseImageItem(successMessage: string | null): void {

        if (relevantFolder !== null) {
            navigate('/items/' + relevantFolder.folderId);
        } else {
            navigate('/items');
        }

        setImageItemsRerenderFlag(new Date());

        setSuccessSnackbarMessage(successMessage);

        setSelectedRows([]);
    }

    function handleCloseFolder(): void {

        navigate('/items');

        setFoldersRerenderFlag(new Date());

        setSelectedRows([]);
    }

    async function handleDeleteAllSelected(): Promise<void> { //could consider parallelizing this later

        updateLastActivity(userContext, localStorageContext);

        let allSuccess: boolean = true;

        for (let i: number = 0; i < selectedRows.length; i++) {

            const selectedRow: string | undefined = selectedRows[i]; 

            if (selectedRow === undefined) {
                continue;
            }

            const commentItem: CommentItem | null = commentItems.find((currentCommentItem: CommentItem) => currentCommentItem.commentItemId === selectedRow) ?? null;

            if (commentItem !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleDeleteCommentItem(commentItem);

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }

            const imageItem: ImageItem | null = imageItems.find((currentImageItem: ImageItem) => currentImageItem.imageItemId === selectedRow) ?? null;

            if (imageItem !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleDeleteImageItem(imageItem);

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }

            const folder: Folder | null = folders.find((currentFolder: Folder) => currentFolder.folderId === selectedRow) ?? null;

            if (folder !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleDeleteFolder(folder);

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }
        }

        if (allSuccess) {
            setSuccessSnackbarMessage('All items deleted successfully');
        }

        setChecklistItemsRerenderFlag(new Date());
        setObservationItemsRerenderFlag(new Date());
        setCommentItemsRerenderFlag(new Date());
        setImageItemsRerenderFlag(new Date());
        setFoldersRerenderFlag(new Date());

        setSelectedRows([]);
    }

    async function handleDeleteFilledChecklist(filledChecklist: FilledChecklist): Promise<boolean> {

        const success: boolean = await indexedDBContext.indexedDBService.removeFilledChecklist(filledChecklist.filledChecklistId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete checklist with name ' + filledChecklist.name);
        }

        setSelectedRows([]);

        return success;
    }

    async function handleDeleteFilledObservation(filledObservation: FilledObservation): Promise<boolean> {

        const success: boolean = await indexedDBContext.indexedDBService.removeFilledObservation(filledObservation.filledObservationId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete observation with name ' + filledObservation.name);
        }

        setSelectedRows([]);

        return success;
    }

    async function handleDeleteCommentItem(commentItem: CommentItem): Promise<boolean> {

        const success: boolean = await indexedDBContext.indexedDBService.removeCommentItem(commentItem.commentItemId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete comment with name ' + commentItem.name);
        }

        setSelectedRows([]);

        return success;
    }

    async function handleDeleteImageItem(imageItem: ImageItem): Promise<boolean> {

        const success: boolean = await indexedDBContext.indexedDBService.removeImageItem(imageItem.imageItemId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete image with name ' + imageItem.name);
        }

        setSelectedRows([]);

        return success;
    }

    async function handleDeleteFolder(folder: Folder): Promise<boolean> {

        const filledChecklistsInFolder: FilledChecklist[] = filledChecklists.filter((filledChecklist: FilledChecklist) => filledChecklist.folderId === folder.folderId);

        for (let i: number = 0; i < filledChecklistsInFolder.length; i++) {

            const currentChecklist: FilledChecklist | undefined = filledChecklistsInFolder[i];

            let success: boolean = false;

            if (currentChecklist !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleDeleteFilledChecklist(currentChecklist);
            }
    
            if (!success) {
                return false;
            }
        }

        const filledObservationsInFolder: FilledObservation[] = filledObservations.filter((filledObservation: FilledObservation) => filledObservation.folderId === folder.folderId);

        for (let i: number = 0; i < filledObservationsInFolder.length; i++) {

            const currentObservation: FilledObservation | undefined = filledObservationsInFolder[i];

            let success: boolean = false;

            if (currentObservation !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleDeleteFilledObservation(currentObservation);
            }
    
            if (!success) {
                return false;
            }
        }

        const commentItemsInFolder: CommentItem[] = commentItems.filter((commentItem: CommentItem) => commentItem.folderId === folder.folderId);

        for (let i: number = 0; i < commentItemsInFolder.length; i++) {

            const currentCommentItem: CommentItem | undefined = commentItemsInFolder[i];

            let success: boolean = false;

            if (currentCommentItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleDeleteCommentItem(currentCommentItem);
            }
    
            if (!success) {
                return false;
            }
        }

        const imageItemsInFolder: ImageItem[] = imageItems.filter((imageItem: ImageItem) => imageItem.folderId === folder.folderId);

        for (let i: number = 0; i < imageItemsInFolder.length; i++) {
            
            const currentImageItem: ImageItem | undefined = imageItemsInFolder[i];

            let success: boolean = false;

            if (currentImageItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleDeleteImageItem(currentImageItem);
            }
    
            if (!success) {
                return false;
            }
        }

        const success: boolean = await indexedDBContext.indexedDBService.removeFolder(folder.folderId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete folder with name ' + folder.folderId);
            return false;
        }

        setSelectedRows([]);

        return true;
    }

    async function handleDeleteOpenedFolder(confirm: boolean): Promise<void> {
        
        setShowConfirmDeletionFolderDialog(false);
        
        if (confirm) {

            if (openedFolder === null) {
                setErrorSnackbarMessage('Could not find opened folder');
                return;
            }

            const success: boolean = await handleDeleteFolder(openedFolder);

            if (success) {
                setSuccessSnackbarMessage('Folder deleted successfully');
            }

            setFoldersRerenderFlag(new Date());
            setChecklistItemsRerenderFlag(new Date());
            setObservationItemsRerenderFlag(new Date());
            setCommentItemsRerenderFlag(new Date());
            setImageItemsRerenderFlag(new Date());

            setSelectedRows([]);

            navigate('/items');
        }
    }

    async function handleSaveAllSelected(deleteFolderAfterUpload: boolean): Promise<void> { //could consider parallelizing this later

        updateLastActivity(userContext, localStorageContext);

        if (openedFolder !== null) { //the folder currently opened should be saved first (if there is one)
            
            if (openedFolder.templateId !== null) { //should not happen as button should be disabled
                return;
            }

            const folderSucess: boolean = await handleSaveFolderWithoutContent(openedFolder);

            if (!folderSucess) {
                return;
            }
        }

        let allSuccess: boolean = true;

        for (let i: number = 0; i < selectedRows.length; i++) {

            const selectedRow: string | undefined = selectedRows[i]; 

            if (selectedRow === undefined) {
                continue;
            }

            const commentItem: CommentItem | null = commentItems.find((currentCommentItem: CommentItem) => currentCommentItem.commentItemId === selectedRow) ?? null;

            if (commentItem !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleSaveCommentItem(commentItem);

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }

            const imageItem: ImageItem | null = imageItems.find((currentImageItem: ImageItem) => currentImageItem.imageItemId === selectedRow) ?? null;

            if (imageItem !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleSaveImageItem(imageItem);

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }

            const folder: Folder | null = folders.find((currentFolder: Folder) => currentFolder.folderId === selectedRow) ?? null;

            if (folder !== null) {
                // eslint-disable-next-line no-await-in-loop
                const success: boolean = await handleSaveFolderWithContent(folder);

                if (success) {
                    if (deleteFolderAfterUpload || folder.templateId !== null) {
                        // eslint-disable-next-line no-await-in-loop
                        await indexedDBContext.indexedDBService.removeFolder(folder.folderId); //it shouldn't be possible that there are still items linked to this folder here, so should be safe
                    }
                }

                if (!success) {
                    allSuccess = false;
                    break;
                }

                continue;
            }
        }

        if (allSuccess) {
            setSuccessSnackbarMessage('All items uploaded successfully');
        }

        setChecklistItemsRerenderFlag(new Date());
        setObservationItemsRerenderFlag(new Date());
        setCommentItemsRerenderFlag(new Date());
        setImageItemsRerenderFlag(new Date());
        setFoldersRerenderFlag(new Date());

        setSelectedRows([]);
    }

    async function handleSaveFilledChecklist(filledChecklist: FilledChecklist): Promise<boolean> {

        const checklistModel: ChecklistModel | null = checklistModels.find((currentChecklistModel: ChecklistModel) => currentChecklistModel.checklistId === filledChecklist.checklistId) ?? null;

        if (checklistModel === null) {
            setErrorSnackbarMessage('Checklist with name ' + filledChecklist.name + ' does not have a corresponding checklist model');
            return false;
        }

        //validation
        for (let i: number = 0; i < checklistModel.elements.length; i++) {

            const element: ChecklistElement | undefined = checklistModel?.elements[i];

            if (element instanceof MaskChecklistElement) {

                const valueObject: FilledChecklistValueObject | null = filledChecklist.values.find((value: FilledChecklistValueObject) => value.elementId === element.elementId) ?? null;

                if (valueObject !== null && valueObject.value !== '' && !(new RegExp('^' + element.regexMask + '$', 'u')).test(valueObject.value ?? '')) {

                    setErrorSnackbarMessage('Cannot upload checklist with name ' + filledChecklist.name + ' as it has an invalid mask input');
                    return false;
                }
            }
        }

        const syncSuccess: boolean = await apiContext.api?.saveFilledChecklist(filledChecklist.checklistId, filledChecklist.name, checklistModel, filledChecklist.values, filledChecklist.folderId, filledChecklist.sortIndex, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload checklist with name ' + filledChecklist.name);
            return false;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeFilledChecklist(filledChecklist.filledChecklistId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete checklist with name ' + filledChecklist.name + ' (but it has been uploaded)');
            return false;
        }

        return true;
    }

    async function handleSaveFilledObservation(filledObservation: FilledObservation): Promise<boolean> {

        const syncSuccess: boolean = await apiContext.api?.saveFilledObservation(filledObservation.observationId, filledObservation.name, filledObservation.ruleSetId, filledObservation.observationLines, filledObservation.folderId, filledObservation.sortIndex, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload observation with name ' + filledObservation.name);
            return false;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeFilledObservation(filledObservation.filledObservationId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete observation with name ' + filledObservation.name + ' (but it has been uploaded)');
            return false;
        }

        return true;
    }

    async function handleSaveCommentItem(commentItem: CommentItem): Promise<boolean> {

        const syncSuccess: boolean = await apiContext.api?.saveCommentItem(commentItem.name, commentItem.createdDate, commentItem.comment, commentItem.folderId, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload comment with name ' + commentItem.name);
            return false;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeCommentItem(commentItem.commentItemId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete comment with name ' + commentItem.name + ' (but it has been uploaded)');
            return false;
        }

        return true;
    }

    async function handleSaveImageItem(imageItem: ImageItem): Promise<boolean> {

        const syncSuccess: boolean = await apiContext.api?.saveImageItem(imageItem.name, imageItem.fileName, imageItem.createdDate, imageItem.imageBlob, imageItem.folderId, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload image with name ' + imageItem.name);
            return false;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeImageItem(imageItem.imageItemId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete image with name ' + imageItem.name + ' (but it has been uploaded)');
            return false;
        }

        return true;
    }

    async function handleSaveFolderWithoutContent(folder: Folder): Promise<boolean> { //folders are not deleted when uploaded

        const syncSuccess: boolean = await apiContext.api?.saveFolder(folder.folderId, folder.templateId, folder.name, folder.createdDate, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload folder with name ' + folder.name);
            return false;
        }

        return true;
    }

    async function handleSaveFolderWithContent(folder: Folder): Promise<boolean> { //folders are not deleted when uploaded

        const folderSucess: boolean = await handleSaveFolderWithoutContent(folder); //first save the folder itself

        if (!folderSucess) {
            return false;
        }

        const filledChecklistsInFolder: FilledChecklist[] = filledChecklists.filter((filledChecklist: FilledChecklist) => filledChecklist.folderId === folder.folderId);

        for (let i: number = 0; i < filledChecklistsInFolder.length; i++) {

            const currentFilledChecklist: FilledChecklist | undefined = filledChecklistsInFolder[i];

            let success: boolean = false;

            if (currentFilledChecklist !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleSaveFilledChecklist(currentFilledChecklist);
            }
    
            if (!success) {
                return false;
            }
        }

        const filledObservationsInFolder: FilledObservation[] = filledObservations.filter((filledObservation: FilledObservation) => filledObservation.folderId === folder.folderId);

        for (let i: number = 0; i < filledObservationsInFolder.length; i++) {

            const currentFilledObservation: FilledObservation | undefined = filledObservationsInFolder[i];

            let success: boolean = false;

            if (currentFilledObservation !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleSaveFilledObservation(currentFilledObservation);
            }
    
            if (!success) {
                return false;
            }
        }

        const commentItemsInFolder: CommentItem[] = commentItems.filter((commentItem: CommentItem) => commentItem.folderId === folder.folderId);

        for (let i: number = 0; i < commentItemsInFolder.length; i++) {

            const currentCommentItem: CommentItem | undefined = commentItemsInFolder[i];

            let success: boolean = false;

            if (currentCommentItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleSaveCommentItem(currentCommentItem);
            }
    
            if (!success) {
                return false;
            }
        }

        const imageItemsInFolder: ImageItem[] = imageItems.filter((imageItem: ImageItem) => imageItem.folderId === folder.folderId);

        for (let i: number = 0; i < imageItemsInFolder.length; i++) {

            const currentImageItem: ImageItem | undefined = imageItemsInFolder[i];

            let success: boolean = false;

            if (currentImageItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await handleSaveImageItem(currentImageItem);
            }
    
            if (!success) {
                return false;
            }
        }

        setSelectedRows([]);

        return true;
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    async function handleSaveOpenedFolderStep1(confirm: boolean): Promise<void> {

        setShowConfirmUploadFolderDialog(false);

        if (confirm) {

            if (openedFolder !== null && openedFolder.templateId !== null) {
                await handleSaveOpenedFolderStep2(true);
            } else {
                setShowConfirmRemoveFolderAfterUploadDialog(true);
            }
        }
    }

    async function handleSaveOpenedFolderStep2(deleteFolderAfterUpload: boolean): Promise<void> {
        
        setShowConfirmRemoveFolderAfterUploadDialog(false);        

        if (openedFolder === null) {
            setErrorSnackbarMessage('Could not find opened folder');
            return;
        }

        const success: boolean = await handleSaveFolderWithContent(openedFolder);

        if (success) {
            setSuccessSnackbarMessage('Folder uploaded successfully');

            if (deleteFolderAfterUpload) {
                await indexedDBContext.indexedDBService.removeFolder(openedFolder.folderId); //it shouldn't be possible that there are still items linked to this folder here, so should be safe
            }
        }

        setFoldersRerenderFlag(new Date());
        setChecklistItemsRerenderFlag(new Date());
        setObservationItemsRerenderFlag(new Date());
        setCommentItemsRerenderFlag(new Date());
        setImageItemsRerenderFlag(new Date());

        setSelectedRows([]);

        if (deleteFolderAfterUpload) {
            navigate('/items');
        }
    }

    return (
        <>
            {openedItem === null &&
                <div style={{
                    height: windowSize[1] - navBarHeight,
                    overflow: 'auto',
                }}>
                    {openedFolder !== null &&
                        <>
                            <AppBar 
                                position='sticky'
                                elevation={0}
                                style={{
                                    background: currentTheme.palette.background.paper,
                                    height: appBarHeight,
                                }}
                            >
                                <Toolbar style={{
                                    justifyContent: 'left',
                                }}>
                                    <IconButton 
                                        edge='end' 
                                        color='inherit' 
                                        onClick={handleCloseFolder}
                                    >
                                        <FontAwesomeIcon
                                            icon={faArrowLeft}
                                            color={currentTheme.palette.text.primary}    
                                        />
                                    </IconButton>
                                    <Typography
                                        noWrap
                                        style={{
                                            marginLeft: '30px',
                                            color: currentTheme.palette.text.primary,
                                            
                                        }}
                                    >
                                        {openedFolder.name}
                                    </Typography>
                                    <IconButton
                                        onClick={(e: React.MouseEvent<HTMLElement>): void => setAnchorElOpenedFolder(e.currentTarget)}
                                        sx={{
                                            marginLeft: 'auto',
                                            marginRight: '0px',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faEllipsisVertical}
                                            color={currentTheme.palette.text.primary}  
                                        />
                                    </IconButton>
                                </Toolbar>
                            </AppBar>

                            <RenameFolderDialog
                                controller={showRenameFolderDialog}
                                onClose={handleCloseRenameFolderDialog}
                                folder={openedFolder}
                            />

                            <FolderInfoDialog
                                controller={showFolderInfoDialog}
                                onClose={(): void => setShowFolderInfoDialog(false)}
                                folder={openedFolder}
                                memoTemplates={memoTemplates}
                            />
                        </>
                    }
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{
                                        padding: '5px 0px 5px 5px',
                                    }}>
                                        <Checkbox
                                            color='primary'
                                            checked={selectedRows.length === allRowIdsInScope.length && allRowIdsInScope.length !== 0}
                                            onChange={(e: React.ChangeEvent): void => toggleSelectAll(e, allRowIdsInScope, setSelectedRows, null)}
                                            onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        Type
                                    </TableCell>
                                    <TableCell>
                                        Name
                                    </TableCell>
                                    <TableCell style={{
                                        textAlign: 'right',
                                    }}>
                                        <IconButton
                                            onClick={(e: React.MouseEvent<HTMLElement>): void => setAnchorElTableHeader(e.currentTarget)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faEllipsisVertical}
                                                color={currentTheme.palette.text.primary}  
                                            />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableRows}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <SpeedDial
                        open={openSpeedDial}
                        onClick={(): void => setOpenSpeedDial(!openSpeedDial)}
                        sx={{
                            position: 'absolute',
                            bottom: 24 + navBarHeight,
                            right: 24,
                        }}
                        icon={<SpeedDialIcon />}
                        ariaLabel='speedDial'
                    >
                        <SpeedDialAction
                            icon={<FontAwesomeIcon icon={faImage} />}
                            tooltipTitle='Image'
                            tooltipOpen={true}
                            onClick={handleAddNewImage}
                        />
                        <SpeedDialAction
                            icon={<FontAwesomeIcon icon={faComment} />}
                            tooltipTitle='Comment'
                            tooltipOpen={true}
                            onClick={handleAddNewComment}
                        />
                        {openedFolder === null &&
                            <SpeedDialAction
                                icon={<FontAwesomeIcon icon={faFolder} />}
                                tooltipTitle='Folder'
                                tooltipOpen={true}
                                onClick={handleAddNewFolder}
                            />
                        }
                    </SpeedDial>

                    <Menu
                        anchorEl={anchorElTableHeader}
                        open={openMenuTableHeader}
                        onClose={(): void => setAnchorElTableHeader(null)}
                        onClick={(): void => setAnchorElTableHeader(null)}
                        transformOrigin={{
                            horizontal: 'right',
                            vertical: 'top',
                        }}
                        anchorOrigin={{
                            horizontal: 'right',
                            vertical: 'bottom',
                        }}
                    >
                        {(openedFolder === null || openedFolder.templateId === null) &&
                            <MenuItem
                                onClick={(): void => setShowConfirmUploadDialog(true)}
                            >
                                Upload Selected
                            </MenuItem>
                        }
                        <MenuItem
                            onClick={(): void => setShowMoveItemsDialog(true)}
                        >
                            Move Selected
                        </MenuItem>
                        <MenuItem
                            onClick={(): void => setShowConfirmDeletionDialog(true)}
                        >
                            Delete Selected
                        </MenuItem>
                    </Menu>

                    <Menu
                        anchorEl={anchorElOpenedFolder}
                        open={openMenuOpenedFolder}
                        onClose={(): void => setAnchorElOpenedFolder(null)}
                        onClick={(): void => setAnchorElOpenedFolder(null)}
                        transformOrigin={{
                            horizontal: 'right',
                            vertical: 'top',
                        }}
                        anchorOrigin={{
                            horizontal: 'right',
                            vertical: 'bottom',
                        }}
                    >
                        <MenuItem
                            onClick={(): void => setShowConfirmUploadFolderDialog(true)}
                        >
                            Upload
                        </MenuItem>
                        <MenuItem
                            onClick={(): void => setShowConfirmDeletionFolderDialog(true)}
                        >
                            Delete
                        </MenuItem>
                        <MenuItem
                            onClick={(): void => setShowRenameFolderDialog(true)}
                        >
                            Rename
                        </MenuItem>
                        <MenuItem
                            onClick={(): void => setShowFolderInfoDialog(true)}
                        >
                            Info
                        </MenuItem>
                    </Menu>

                    <AddFolderDialog
                        controller={showAddFolderDialog}
                        onClose={handleCloseAddFolderDialog}
                        memoTemplates={memoTemplates}
                        checklistModels={checklistModels}
                        observationModels={observationModels}
                    />

                    <MoveItemsDialog
                        controller={showMoveItemsDialog}
                        onClose={handleCloseMoveItemsDialog}
                        openedFolder={openedFolder}
                        selectedRows={selectedRows}
                        folders={folders}
                        commentItems={commentItems}
                        imageItems={imageItems}
                    />

                    <ConfirmDialog 
                        controller={showConfirmDeletionDialog}
                        onClose={async (confirm: boolean): Promise<void> => {
                            setShowConfirmDeletionDialog(false);
                            if (confirm) {
                                await handleDeleteAllSelected();
                            }
                        }}
                        title='Delete all selected items'
                        text='Are you sure you want to delete all the items you have selected?'
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ConfirmDialog 
                        controller={showConfirmUploadDialog}
                        onClose={async (confirm: boolean): Promise<void> => {
                            setShowConfirmUploadDialog(false);
                            if (confirm) {

                                let selectedItemsContainsFolders: boolean = false;

                                for (let i: number = 0; i < folders.length; i++) {
                                    selectedItemsContainsFolders = selectedRows.includes(folders[i]?.folderId ?? '');

                                    if (selectedItemsContainsFolders) {
                                        break;
                                    }
                                }

                                if (selectedItemsContainsFolders) {
                                    setShowConfirmRemoveAllSelectedFoldersAfterUploadDialog(true);
                                } else {
                                    await handleSaveAllSelected(false);
                                }
                            }
                        }}
                        title='Upload all selected items'
                        text='Are you sure you want to upload all the items you have selected? You can no longer edit them from this app.'
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ConfirmDialog 
                        controller={showConfirmRemoveAllSelectedFoldersAfterUploadDialog}
                        onClose={async (confirm: boolean): Promise<void> => {
                            setShowConfirmRemoveAllSelectedFoldersAfterUploadDialog(false);
                            await handleSaveAllSelected(confirm);
                        }}
                        title='Remove all selected folders after upload'
                        text='Do you want to remove the selected folders from this device after they have been uploaded? Any folders associated with a template will be deleted after upload independently of this choice.'
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ConfirmDialog 
                        controller={showConfirmUploadFolderDialog}
                        onClose={handleSaveOpenedFolderStep1}
                        title='Upload folder'
                        text={'Are you sure you want to upload this folder and all of its content?' + (openedFolder?.templateId !== null ? ' As this folder is associated with a template, it will automatically be deleted once uploaded.' : '')}
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ConfirmDialog 
                        controller={showConfirmRemoveFolderAfterUploadDialog}
                        onClose={handleSaveOpenedFolderStep2}
                        title='Remove folder after upload'
                        text='Do you want to remove the folder from this device after it has been uploaded?'
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ConfirmDialog 
                        controller={showConfirmDeletionFolderDialog}
                        onClose={handleDeleteOpenedFolder}
                        title='Delete folder'
                        text='Are you sure you want to delete this folder and all of its content?'
                        noText='No'
                        yesText='Yes'
                        loadingConfirm={true}
                    />

                    <ErrorSnackbar 
                        message={errorSnackbarMessage}
                        setMessage={setErrorSnackbarMessage}
                    />

                    <SuccessSnackbar 
                        message={successSnackbarMessage}
                        setMessage={setSuccessSnackbarMessage}
                    />
                </div>
            }
            {openedItem !== null && openedItem.itemType === ItemType.CHECKLIST && openedItem.parentId !== null &&
                <ChecklistItemSubTab
                    filledChecklistId={openedItem.itemId}
                    checklistId={openedItem.parentId}
                    sortIndex={openedItem.sortIndex ?? -1}
                    closeSubTab={handleCloseFilledChecklist}
                />
            }
            {openedItem !== null && openedItem.itemType === ItemType.OBSERVATION && openedItem.parentId !== null &&
                <ObservationItemSubTab
                    filledObservationId={openedItem.itemId}
                    observationId={openedItem.parentId}
                    sortIndex={openedItem.sortIndex ?? -1}
                    closeSubTab={handleCloseFilledObservation}
                />
            }
            {openedItem !== null && openedItem.itemType === ItemType.COMMENT &&
                <CommentItemSubTab
                    commentItemId={openedItem.itemId}
                    closeSubTab={handleCloseCommentItem}
                />
            }
            {openedItem !== null && openedItem.itemType === ItemType.IMAGE &&
                <ImageItemSubTab
                    imageItemId={openedItem.itemId}
                    closeSubTab={handleCloseImageItem}
                />
            }
        </>
    );
}

export default ItemsTab;